// src/pages/DesignPage.js

import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { reverseGeocode } from "../api/reverseGeocodeClient";
import { createNamedProject } from "../api/ProjectManager";

import "./NewProjectPage.css";
import AWS from "aws-sdk";

export default function NewProjectPage() {
  const navigate = useNavigate();
  const { user, profile } = useOutletContext();
  const [isCreating, setIsCreating] = useState(false);

  const [propertyId, setPropertyId] = useState("");
  const [address, setAddress] = useState("");
  const [townStateZip, setTownStateZip] = useState("");

  // ─── Redirect if not logged in ─────────────────────────────────────
  useEffect(() => {
    if (!user) {
      navigate("/profile", { replace: true });
    }
  }, [user, navigate]);
  // ───────────────────────────────────────────────────────────────────

  const [projectName, setProjectName] = useState("");
  const [location, setLocation] = useState("");
  const [error, setError] = useState("");

  async function handleGetLocation() {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async ({ coords: { latitude, longitude } }) => {
        try {
          const loc = await reverseGeocode(latitude, longitude);
          console.log("get location", loc.propertyId);
          setPropertyId(`${loc.propertyId}`);
          setAddress(`${loc.house_number || ""} ${loc.road || ""}`.trim());
          setTownStateZip(
            `${loc.town || ""}, ${loc.stateCode || ""}, ${loc.postcode || ""}`,
          );
          setProjectName(`${loc.house_number || ""} ${loc.road || ""}`.trim());
          setError("");
        } catch {
          setError("Failed to retrieve address details.");
        }
      },
      () => setError("Unable to retrieve your location."),
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setIsCreating(true);
    const userId = AWS.config.credentials.identityId;
    const awsCreds = AWS.config.credentials;

    try {
      const payload = await createNamedProject(
        userId,
        projectName,
        { propertyId, address, townStateZip },
        awsCreds,
      );

      console.log("Project created:", payload);

      // Navigate directly to Design Harmony with project context
      navigate("/design", {
        state: { projectId: payload.id, s3_prefix: payload.s3_prefix },
      });
    } catch (err) {
      console.error(err);
      setError("Failed to create project. Please try again.");
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <div className="design-page min-h-screen bg-white px-6 py-12">
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6 text-center">
          Start a New Project
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Property */}
          <div className="mb-4">
            <div className="property-header">
              <label htmlFor="address" className="font-medium">
                Property
              </label>
              <button
                type="button"
                onClick={handleGetLocation}
                className="btn-get-location"
              >
                Get My Location
              </button>
            </div>

            {/* place_id (read-only) */}
            <input
              type="text"
              id="propertyId"
              value={propertyId}
              disabled
              placeholder="Place ID"
              className="w-full mb-2 border rounded px-3 py-2 bg-gray-100"
            />

            {/* Address */}
            <input
              type="text"
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Address (house number & road)"
              required
              className="w-full mb-2 border rounded px-3 py-2"
            />

            {/* Town, State */}
            <input
              type="text"
              id="townStateZip"
              value={townStateZip}
              onChange={(e) => setTownStateZip(e.target.value)}
              placeholder="Town, State"
              required
              className="w-full mb-2 border rounded px-3 py-2"
            />

            {error && <p className="mt-1 text-red-500 text-sm">{error}</p>}
          </div>

          {/* Project Name */}
          <div>
            <label className="block mb-1 font-medium">Project Name</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              required
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Submit */}
          <div className="text-center">
            <button type="submit" disabled={isCreating} className="btn-create">
              {isCreating ? "Creating…" : "Create Project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
